import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import FormItemContainer from './../Global/Containers/FormItemContainer';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import classnames from 'classnames';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import AssignedContactItem from './ContactItem';
import AssignContactsModal from './AssignContactsModal';
import LoadingIndicator from '../../components/LoadingIndicator';
import * as Mutations from '../../graphQL/mutations/locationMutations';
import * as ContactMutations from '../../graphQL/mutations/contactMutations';
import ContactForm from '../../components/contacts/ContactForm';
import ContactModel from '../../Models/Contact';
import _pick from 'lodash/pick';
import TypographyStyles from '../../styles/typography';
import combineStyles from '../../utils/combineStyles';
import NotificationSnackbar from '../common/NotificationSnackbar';

const styles = theme => ({
  contactsContainer: {
    '& :last-child': {
      borderBottom: 'none !important',
      marginBottom: 0
    }
  },
  assignedContactsList: {
    paddingLeft: theme.spacing.unit * 3,
    paddingRight: theme.spacing.unit * 3,
    paddingTop: 0,
    paddingBottom: theme.spacing.unit * 3
  },
  emptyContainer: {
    minHeight: '268px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  noContacts: {
    flex: 'none !important'
  }
});
export class AssignedContacts extends PureComponent {
  static propTypes = {
    ApolloClient: PropTypes.object.isRequired,
    assignedContacts: PropTypes.array.isRequired,
    contactsList: PropTypes.array.isRequired,
    locationId: PropTypes.string.isRequired,
    primaryContactId: PropTypes.number,
    contactsLoading: PropTypes.bool.isRequired,
    onUpdate: PropTypes.func.isRequired,
    rowVersion: PropTypes.string.isRequired,
    orgId: PropTypes.string.isRequired,
    divisionId: PropTypes.string.isRequired
  };
  state = {
    displayAssignContactsModal: false,
    displayContactForm: false,
    contact: ContactModel(),
    errors: null
  };
  _handleDisplayAddContactForm = () => {
    this.setState({
      displayContactForm: true,
      contact: ContactModel()
    });
  };
  _handleEditContactForm = contact => {
    this.setState({ displayContactForm: true, contact });
  };
  _toggleModalDisplay = modalType => {
    const currentModalStatus = this.state[modalType];
    this.setState({ [modalType]: !currentModalStatus, menuElement: null });
  };
  _handleErrorOnClose = () => {
    this.setState({ errors: null });
  };
  _handleSetPrimaryContact = async contactId => {
    const { rowVersion, ApolloClient, locationId, onUpdate } = this.props;

    try {
      await ApolloClient.mutate({
        mutation: Mutations.setPrimaryContactMutation,
        variables: {
          primaryContactInfo: {
            contactId,
            locationId,
            rowVersion
          }
        }
      });
      onUpdate('assignedContactsLoading');
      return true;
    } catch (e) {
      this.setState({ errors: e });
      return false;
    }
  };
  _handleRemoveAssignedContact = async locationContactLinkId => {
    const { ApolloClient, onUpdate } = this.props;

    try {
      await ApolloClient.mutate({
        mutation: Mutations.unlinkContactMutation,
        variables: {
          locationContactLinkId: locationContactLinkId
        }
      });
      onUpdate('assignedContactsLoading');
      return true;
    } catch (e) {
      this.setState({ errors: e });
      return false;
    }
  };
  _handleAssignContacts = async contacts => {
    const { ApolloClient, locationId } = this.props;

    try {
      await ApolloClient.mutate({
        mutation: Mutations.linkContactMutation,
        variables: {
          linkInfo: {
            contactIds: contacts,
            locationId
          }
        }
      });
      await this.props.onUpdate('assignedContactsLoading');
      return true;
    } catch (e) {
      this.setState({ errors: e });
      return false;
    }
  };
  _handleContactSave = async contact => {
    const { ApolloClient, orgId, divisionId } = this.props;
    const isNewContact = contact.id ? false : true;
    if (isNewContact) {
      try {
        const contactCreateObject = _pick(contact, [
          'firstName',
          'lastName',
          'email',
          'phone'
        ]);
        const {
          data: {
            contact: {
              create: { id }
            }
          }
        } = await ApolloClient.mutate({
          mutation: ContactMutations.createContactMutation,
          variables: {
            contact: { ...contactCreateObject, orgId, divisionId }
          }
        });
        await this._handleAssignContacts([id]);
        return true;
      } catch (e) {
        this.setState({ errors: e });
        return false;
      }
    } else {
      const contactObject = _pick(contact, [
        'firstName',
        'lastName',
        'email',
        'phone',
        'rowVersion'
      ]);
      contactObject.id = contact.id;
      contactObject.expired = contact.active ? false : true;
      try {
        await ApolloClient.mutate({
          mutation: ContactMutations.updateContactMutation,
          variables: {
            contact: contactObject
          }
        });
        await this.props.onUpdate('assignedContactsLoading');
        return true;
      } catch (e) {
        this.setState({ errors: e });
        return false;
      }
    }
  };

  editButton = () => {
    const { classes, t } = this.props;
    return (
      <Fragment>
        <Button
          className={classnames(classes.button, classes.editButton)}
          onClick={() => {
            this._handleDisplayAddContactForm();
          }}
          color={'primary'}
          variant={'outlined'}
          classes={{
            label: classes.outlinedButton,
            root: classes.buttonRoot
          }}
        >
          {t('Create New Contact')}
        </Button>
        <IconButton
          key="add"
          aria-label="Add"
          color="primary"
          onClick={() => {
            this._toggleModalDisplay('displayAssignContactsModal');
          }}
          classes={{ root: classes.noPadding }}
        >
          <AddIcon />
        </IconButton>
      </Fragment>
    );
  };
  render() {
    const {
      t,
      classes,
      assignedContacts,
      contactsList,
      contactsLoading,
      primaryContactId
    } = this.props;
    const { displayAssignContactsModal, displayContactForm } = this.state;

    return (
      <Fragment>
        <FormItemContainer
          title={t('Assigned Contacts')}
          secondaryAction={this.editButton()}
          classes={{ contents: classes.assignedContactsList }}
        >
          <div className={classes.contactsContainer}>
            {assignedContacts.map((contactLink, index) => {
              return (
                <AssignedContactItem
                  contact={contactLink.contact}
                  locationContactLinkId={contactLink.locationContactLinkId}
                  key={index}
                  primaryContactId={primaryContactId}
                  onSetPrimaryContact={this._handleSetPrimaryContact}
                  onEditClick={this._handleEditContactForm}
                  onRemoveContactClick={this._handleRemoveAssignedContact}
                />
              );
            })}
          </div>
          {assignedContacts.length === 0 && (
            <div className={classes.emptyContainer}>
              <p
                className={classnames(
                  classes.noContacts,
                  classes.subtitle2,
                  classes.normalWeight
                )}
              >
                {t('No contacts assigned')}.
              </p>
            </div>
          )}
          {contactsLoading && <LoadingIndicator />}
        </FormItemContainer>
        <AssignContactsModal
          open={displayAssignContactsModal}
          allContacts={contactsList}
          assignedContacts={assignedContacts}
          onClose={() => {
            this._toggleModalDisplay('displayAssignContactsModal');
          }}
          onSave={this._handleAssignContacts}
        />
        <ContactForm
          open={displayContactForm}
          onClose={() => {
            this._toggleModalDisplay('displayContactForm');
          }}
          onSave={this._handleContactSave}
          contact={this.state.contact}
        />
        <NotificationSnackbar
          open={Boolean(this.state.errors)}
          handleSnackbarClose={this._handleErrorOnClose}
          message={t('Failed to update location data')}
        />
      </Fragment>
    );
  }
}
const translatedAssignedContacts = withNamespaces()(AssignedContacts);
export default withStyles(combineStyles(styles, TypographyStyles))(
  translatedAssignedContacts
);
